import { clsx } from 'clsx';

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
}

export function Skeleton({
  className,
  variant = 'text',
  width,
  height,
}: SkeletonProps) {
  const variantStyles = {
    text: 'rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
  };

  return (
    <div
      className={clsx(
        'animate-pulse bg-gray-200',
        variantStyles[variant],
        className
      )}
      style={{
        width: width,
        height: height ?? (variant === 'text' ? '1em' : undefined),
      }}
    />
  );
}

// Preset skeleton components
export function SkeletonText({ lines = 1, className }: { lines?: number; className?: string }) {
  return (
    <div className={clsx('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          variant="text"
          className="h-4"
          width={i === lines - 1 && lines > 1 ? '70%' : '100%'}
        />
      ))}
    </div>
  );
}

export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div className={clsx('p-4 border rounded-lg', className)}>
      <div className="flex items-start space-x-3">
        <Skeleton variant="circular" width={40} height={40} />
        <div className="flex-1 space-y-2">
          <Skeleton variant="text" className="h-5 w-3/4" />
          <Skeleton variant="text" className="h-4 w-1/2" />
        </div>
      </div>
      <div className="mt-4 space-y-2">
        <Skeleton variant="rectangular" height={60} />
      </div>
    </div>
  );
}

// Station marker skeleton - grey pulsing circle
export function StationMarkerSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={clsx(
        'w-8 h-8 rounded-full bg-gray-300 animate-pulse',
        'shadow-md border-2 border-white',
        className
      )}
    />
  );
}

// Price card skeleton - grey bars for price and label
export function PriceCardSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={clsx(
        'p-4 rounded-xl border-2 border-gray-200 bg-gray-50',
        className
      )}
    >
      {/* Fuel type label placeholder */}
      <div className="flex items-center gap-2 mb-2">
        <Skeleton variant="circular" width={10} height={10} />
        <Skeleton variant="text" className="h-3 w-12" />
      </div>
      {/* Price placeholder */}
      <Skeleton variant="text" className="h-8 w-24 mb-1" />
      {/* "per litre" placeholder */}
      <Skeleton variant="text" className="h-3 w-14" />
      {/* Update time placeholder */}
      <Skeleton variant="text" className="h-3 w-20 mt-2" />
    </div>
  );
}

// Chart skeleton - grey rectangle with faint grid lines
export function ChartSkeleton({ 
  height = 200, 
  className 
}: { 
  height?: number; 
  className?: string;
}) {
  return (
    <div
      className={clsx(
        'relative rounded-lg bg-gray-100 overflow-hidden',
        className
      )}
      style={{ height }}
    >
      {/* Faint grid lines */}
      <svg
        className="absolute inset-0 w-full h-full opacity-30"
        preserveAspectRatio="none"
      >
        {/* Horizontal grid lines */}
        {[20, 40, 60, 80].map((y) => (
          <line
            key={`h-${y}`}
            x1="0"
            y1={`${y}%`}
            x2="100%"
            y2={`${y}%`}
            stroke="#9CA3AF"
            strokeWidth="1"
            strokeDasharray="4 4"
          />
        ))}
        {/* Vertical grid lines */}
        {[14, 28, 42, 56, 70, 84].map((x) => (
          <line
            key={`v-${x}`}
            x1={`${x}%`}
            y1="0"
            x2={`${x}%`}
            y2="100%"
            stroke="#9CA3AF"
            strokeWidth="1"
            strokeDasharray="4 4"
          />
        ))}
      </svg>
      
      {/* Animated shimmer overlay */}
      <div
        className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-gray-200/50 to-transparent"
        style={{
          animation: 'shimmer 2s infinite',
        }}
      />
      
      {/* Loading text */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm text-gray-400">Loading price history...</span>
      </div>
    </div>
  );
}

// Drawer content skeleton for when station data is loading
export function StationDrawerSkeleton({ className }: { className?: string }) {
  return (
    <div className={clsx('p-4 space-y-6', className)}>
      {/* Header skeleton */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <Skeleton variant="circular" width={40} height={40} />
          <div className="flex-1">
            <Skeleton variant="text" className="h-6 w-3/4 mb-1" />
            <Skeleton variant="text" className="h-4 w-1/2" />
          </div>
        </div>
      </div>

      {/* Price cards skeleton */}
      <div>
        <Skeleton variant="text" className="h-4 w-24 mb-3" />
        <div className="grid grid-cols-2 gap-3">
          <PriceCardSkeleton />
          <PriceCardSkeleton />
        </div>
      </div>

      {/* Chart skeleton */}
      <div>
        <Skeleton variant="text" className="h-4 w-28 mb-3" />
        <ChartSkeleton height={180} />
      </div>

      {/* Info skeleton */}
      <div className="space-y-3">
        <Skeleton variant="text" className="h-4 w-20" />
        <Skeleton variant="rectangular" height={80} />
      </div>
    </div>
  );
}
